import React, { forwardRef } from 'react';
import { toast as fireToast, ToastContainer as ToastifyContainer, Slide, } from 'react-toastify';
import { expandFontTheme, IconClose, IconCloseOutline, IconConfirmation, IconConfirmationOutline, IconVulnerability, IconVulnerabilityOutline, IconWarning, IconWarningOutline, mapCssValue, theme, darkTheme, useLocalizationContext, useThemeContext, getThemeValue, addFontThemePrefix, } from '@contrast-security-inc/design-system-foundations';
import * as messages from './translations';
import { css, cx } from '@emotion/css';
// Styles being overridden originated from 'react-toastify/dist/ReactToastify.css'
const toastContainerStyles = (selectedTheme) => css `
  height: 0;
  position: absolute;
  width: 0;
  z-index: ${theme.zIndex.toast};

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__toast-container {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    left: 50%;
    position: fixed;
    transform: translateX(-50%);
    width: auto;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__toast {
    ${expandFontTheme(addFontThemePrefix(selectedTheme, 'body'))};
    align-self: center;
    border-radius: 8px;
    box-shadow: ${theme.shadow.lt};
    box-sizing: border-box;
    cursor: default;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    max-width: 850px;
    min-height: auto;
    min-width: 320px;
    overflow: hidden;
    padding: 0;
    position: relative;
    transition: 0.2s ease all;
    transition: flex 0.3s ease;
    z-index: ${theme.zIndex.toast};

    a {
      margin-left: 4px;
    }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__toast-body {
    display: flex;
    margin: 0;
    padding: 0;
    width: 100%;

    > div:last-child {
      flex: 1;
    }
  }

  /* stylelint-disable-next-line selector-class-pattern, selector-list-comma-newline-after */
  .Toastify__slide-enter--top-center, .Toastify__slide-exit--top-center {
    animation-fill-mode: both;
    animation-duration: 0.3s;
  }

  @keyframes toast-slide-in-down {
    from {
      opacity: 0;
      transform:
        translateY(10px)
        scale(0.95);
    }

    to {
      opacity: 1;
      transform:
        translateY(0)
        scale(1);
    }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__slide-enter--top-center {
    animation-name: toast-slide-in-down;
  }

  @keyframes toast-slide-out-up {
    from {
      opacity: 1;
      transform:
        translateY(0)
        scale(1);
    }

    to {
      opacity: 0;
      transform:
        translateY(-10px)
        scale(0.95);
    }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__slide-exit--top-center {
    animation-name: toast-slide-out-up;
  }

  /** Required for proper animation */
  @keyframes toast-track-progress {
    0% { transform: scaleX(1); }
    100% { transform: scaleX(0); }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__progress-bar--animated {
    animation: toast-track-progress linear 1 forwards;
  }

  @media only screen and (max-width: 480px) {
    /* stylelint-disable-next-line selector-class-pattern */
    .Toastify__toast-container {
      left: 0;
      margin: 0 0 0 1vw;
      padding: 0;
      width: 98vw;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .Toastify__toast {
      margin-bottom: 8px;
    }

    /* stylelint-disable-next-line selector-class-pattern */
    .Toastify__toast-container--top-center {
      top: 0;
      transform: translateX(0);
    }
  }
`;
const toastCustomTopStyles = (topOffset) => css `
  /* stylelint-disable-next-line selector-class-pattern */
  .Toastify__toast-container {
    top: ${mapCssValue(topOffset)};
  }
`;
const toastContentWrapperStyles = (selectedTheme) => css `
  border: 1px solid transparent;
  color: ${theme.color.white};
  display: flex;
  gap: 24px;
  padding: 16px;
  ${selectedTheme === 'dark' &&
    `
    border-radius: 8px;
  `}

  /* stylelint-disable-next-line no-descending-specificity */
  a,
  a:hover {
    color: ${theme.color.white};
    text-decoration: underline;
  }
`;
const toastContentStyles = css `
  display: flex;
  flex: 1;
  gap: 8px;
  line-height: 1.3;
  word-break: break-word;

  span {
    flex: 1;
  }
`;
const confirmationStyles = (selectedTheme) => css `
  background-color: ${getThemeValue(selectedTheme, { light: theme.color.green90, dark: darkTheme.color.dimGreen })};
  color: ${getThemeValue(selectedTheme, { light: theme.color.white, dark: darkTheme.color.green100 })};
  ${selectedTheme === 'dark' &&
    `
    border-color: ${darkTheme.color.green100};
  `}
`;
const warningStyles = (selectedTheme) => css `
  background-color: ${getThemeValue(selectedTheme, { light: theme.color.yellow50, dark: darkTheme.color.dimOrange })};
  color: ${getThemeValue(selectedTheme, { light: theme.color.black, dark: darkTheme.color.orange })};
  ${selectedTheme === 'dark' &&
    `
    border-color: ${darkTheme.color.orange};
  `}

  /* stylelint-disable-next-line no-descending-specificity */
  ${selectedTheme === 'light' &&
    `
    a {
      color: ${theme.color.black};

      &:hover {
        color: ${theme.color.neutral80};
      }
    }
  `}
`;
const errorStyles = (selectedTheme) => css `
  background-color: ${getThemeValue(selectedTheme, { light: theme.color.red70, dark: darkTheme.color.dimRed })};
  color: ${getThemeValue(selectedTheme, { light: theme.color.white, dark: darkTheme.color.red100 })};
  ${selectedTheme === 'dark' &&
    `
    border-color: ${darkTheme.color.red100};
  `}
`;
const closeToastStyles = (variant, selectedTheme) => {
    const lightThemeHoverColor = variant === 'warning' ? theme.color.neutral80 : theme.color.neutral40;
    return css `
  align-content: center;
  align-self: flex-start;
  background: ${theme.color.transparent};
  border: none;
  cursor: pointer;
  display: flex;
  margin: 0;
  outline: none;
  padding: 0;

  &:hover svg {
    ${selectedTheme === 'dark'
        ? `
      stroke: ${darkTheme.color.white};
    `
        : `
      fill: ${lightThemeHoverColor};
    `}
  }
`;
};
const getToastVariantStyles = (variant, selectedTheme) => {
    switch (variant) {
        case 'confirmation':
            return confirmationStyles(selectedTheme);
        case 'warning':
            return warningStyles(selectedTheme);
        case 'error':
            return errorStyles(selectedTheme);
    }
};
const ToastIconByVariant = ({ selectedTheme, variant = 'confirmation', iconColor, testId, }) => {
    if (selectedTheme === 'dark') {
        switch (variant) {
            case 'confirmation':
                return React.createElement(IconConfirmationOutline, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
            case 'warning':
                return React.createElement(IconVulnerabilityOutline, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
            case 'error':
                return React.createElement(IconWarningOutline, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
        }
    }
    switch (variant) {
        case 'confirmation':
            return React.createElement(IconConfirmation, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
        case 'warning':
            return React.createElement(IconVulnerability, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
        case 'error':
            return React.createElement(IconWarning, { size: "md", color: iconColor, testId: testId && `${testId}-icon` });
    }
};
const ToastContent = ({ closeToast, content, options }) => {
    const { selectedTheme } = useThemeContext();
    const { showIcon = true, variant = 'confirmation', testId, onCloseClick } = options ?? {};
    const iconColorLightTheme = variant === 'warning' ? 'black' : 'white';
    const iconColorDarkTheme = () => {
        switch (variant) {
            case 'warning':
                return 'darkTheme.orange';
            case 'error':
                return 'darkTheme.red100';
            case 'confirmation':
            default:
                return 'darkTheme.green100';
        }
    };
    const { formatMessage } = useLocalizationContext();
    return (React.createElement("div", { className: cx(toastContentWrapperStyles(selectedTheme), getToastVariantStyles(variant, selectedTheme), 'ds-toast-content'), "data-testid": testId },
        React.createElement("span", { className: toastContentStyles },
            showIcon && (React.createElement(ToastIconByVariant, { variant: variant, selectedTheme: selectedTheme, iconColor: getThemeValue(selectedTheme, {
                    light: iconColorLightTheme,
                    dark: iconColorDarkTheme(),
                }), testId: testId })),
            React.createElement("span", null, content)),
        React.createElement("button", { "aria-label": formatMessage(messages, 'close'), className: closeToastStyles(variant, selectedTheme), "data-testid": testId && `${testId}-close`, onClick: () => {
                if (onCloseClick) {
                    onCloseClick();
                }
                if (closeToast) {
                    closeToast();
                }
            }, title: formatMessage(messages, 'close'), type: "button" }, selectedTheme === 'dark' ? (React.createElement(IconCloseOutline, { size: "md", color: getThemeValue(selectedTheme, {
                light: iconColorLightTheme,
                dark: iconColorDarkTheme(),
            }) })) : (React.createElement(IconClose, { size: "md", color: getThemeValue(selectedTheme, {
                light: iconColorLightTheme,
                dark: iconColorDarkTheme(),
            }) })))));
};
export const toast = (content, options) => {
    const { containerId = 'base-container' } = options ?? {};
    fireToast(React.createElement(ToastContent, { content: content, options: options }), { containerId });
};
/** Dismiss all active toasts */
toast.dismiss = fireToast.dismiss;
/** Clear waiting queue of toasts beyond the current limit */
toast.clearWaitingQueue = (containerId = 'base-container') => {
    fireToast.clearWaitingQueue({ containerId });
};
export const ToastContainer = forwardRef(({ containerId = 'base-container', testId, topOffset = 60, ...rest }, ref) => {
    const { selectedTheme } = useThemeContext();
    React.useLayoutEffect(() => {
        let containerIsDuplicate = false;
        const toastContainers = document.querySelectorAll('.Toastify');
        const containerIdSet = new Set();
        toastContainers.forEach((container) => {
            // check if this container id already exists in the Set
            if (container.id === containerId) {
                containerIsDuplicate = containerIdSet.has(containerId);
            }
            containerIdSet.add(container.id);
        });
        if (containerIsDuplicate) {
            throw new Error(`\n\nDuplicate <ToastContainer /> found! Duplicate container ID is: '${containerId}'\n`);
        }
    }, [containerId]);
    return (React.createElement("div", { className: cx(toastContainerStyles(selectedTheme), toastCustomTopStyles(topOffset), 'ds-toast-container'), "data-testid": testId },
        React.createElement(ToastifyContainer, { autoClose: 5000, closeButton: false, closeOnClick: false, containerId: containerId, draggable: false, enableMultiContainer: true, hideProgressBar: true, limit: 6, pauseOnFocusLoss: false, pauseOnHover: true, position: "top-center", ref: ref, transition: Slide, ...rest })));
});
ToastContainer.displayName = 'ToastContainer';
